// Bundle off-canvas drawer custom element.
// Replaces variant picker + ATC for products in the "bundles" collection.
// Opens a panel with N variant options (each rendered as image thumbs),
// shows bonus products from shophelten.bundle_ekstra_product metafield,
// and adds main variant + all available bonus variants atomically via
// /cart/add.js with items[], then opens the existing cart-drawer.

class BundleDrawer extends HTMLElement {
  constructor() {
    super();

    this.dialog = this.querySelector('.bundle-drawer__dialog');
    this.panel = this.querySelector('.bundle-drawer__panel');
    this.openButton = this.querySelector('[data-bundle-open]');
    this.closeButtons = this.querySelectorAll('[data-bundle-close]');
    this.submitButton = this.querySelector('[data-bundle-submit]');
    this.submitLabel = this.querySelector('[data-bundle-submit-label]');
    this.priceWrapper = this.querySelector('[data-bundle-price]');
    this.priceValue = this.querySelector('[data-bundle-price-value]');
    this.compareValue = this.querySelector('[data-bundle-compare-value]');
    this.stockEl = this.querySelector('[data-bundle-stock]');
    this.stockLabel = this.querySelector('[data-bundle-stock-label]');
    this.errorEl = this.querySelector('[data-bundle-error]');

    this.stockTextIn = this.stockEl?.dataset.inLabel || '';
    this.stockTextOut = this.stockEl?.dataset.outLabel || '';

    this.optionFieldsets = Array.from(this.querySelectorAll('[data-bundle-option]'));
    this.optionInputs = Array.from(this.querySelectorAll('[data-bundle-option-input]'));

    this.variants = this.parseJson('[data-bundle-variants]') || [];
    this.bonusItems = this.parseJson('[data-bundle-bonus]') || [];

    this.selectedOptions = this.optionFieldsets.map(fieldset => {
      const checked = fieldset.querySelector('input:checked');
      return checked ? checked.value : null;
    });

    this.currentVariant = null;
    this.clearBondStyles = (this.dataset.clearBondStyles || '')
      .split('||')
      .map(style => style.trim())
      .filter(Boolean);

    this.moveDialogToBody();
    this.bindEvents();
    this.updateVariant();
  }

  moveDialogToBody() {
    if (!this.dialog) return;
    if (this.dialog.parentElement === document.body) return;
    document.body.appendChild(this.dialog);
  }

  disconnectedCallback() {
    if (this.dialog && this.dialog.parentElement === document.body) {
      this.dialog.remove();
    }
  }

  parseJson(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[bundle-drawer] Invalid JSON in', selector, e);
      return null;
    }
  }

  bindEvents() {
    this.openButton?.addEventListener('click', () => this.open());
    this.closeButtons.forEach(btn => btn.addEventListener('click', () => this.close()));
    this.optionInputs.forEach(input => {
      input.addEventListener('change', event => this.onOptionChange(event));
    });
    this.submitButton?.addEventListener('click', () => this.addBundleToCart());
    this.dialog?.addEventListener('click', event => {
      if (!this.isOpen()) return;
      if (this.panel && !this.panel.contains(event.target)) {
        this.close();
      }
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.isOpen()) this.close();
    });
  }

  isOpen() {
    return this.dialog && this.dialog.classList.contains('is-visible');
  }

  open() {
    if (!this.dialog) return;
    if (this.openButton?.disabled) return;
    this.dialog.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    this.openButton?.setAttribute('aria-expanded', 'true');
    this.dialog.addEventListener(
      'transitionend',
      () => this.panel?.focus(),
      { once: true }
    );
  }

  close() {
    if (!this.dialog) return;
    this.dialog.classList.remove('is-visible');
    document.body.style.overflow = '';
    this.openButton?.setAttribute('aria-expanded', 'false');
    this.openButton?.focus();
  }

  onOptionChange(event) {
    const fieldset = event.target.closest('[data-bundle-option]');
    if (!fieldset) return;

    const positionAttr = fieldset.dataset.optionPosition;
    const position = parseInt(positionAttr, 10);
    if (Number.isNaN(position)) return;

    this.selectedOptions[position - 1] = event.target.value;

    const label = fieldset.querySelector('[data-selected-label]');
    if (label) label.textContent = event.target.value;

    this.updateVariant();
  }

  findVariant() {
    if (this.selectedOptions.some(v => v == null)) return null;

    return this.variants.find(variant =>
      this.selectedOptions.every((value, index) => variant.options[index] === value)
    ) || null;
  }

  updateVariant() {
    this.currentVariant = this.findVariant();
    this.updatePrice();
    this.updateStock();
    this.updateSubmitState();
    this.updateClearBondNote();
    this.clearError();
  }

  updateClearBondNote() {
    if (!this.dialog || !this.clearBondStyles?.length) return;

    const active = this.selectedOptions.some(value => this.clearBondStyles.includes(value));
    this.dialog.querySelectorAll('[data-clear-bond-item]').forEach(item => {
      const title = item.querySelector('[data-bond-title]');
      if (!title) return;
      const original = title.dataset.originalTitle || title.textContent;
      title.textContent = active && title.dataset.clearTitle ? title.dataset.clearTitle : original;
    });
  }

  updateStock() {
    if (!this.stockEl || !this.stockLabel) return;
    const available = !!(this.currentVariant && this.currentVariant.available);
    this.stockEl.classList.toggle('is-unavailable', !available);
    this.stockLabel.textContent = available ? this.stockTextIn : this.stockTextOut;
  }

  updatePrice() {
    if (!this.currentVariant) return;
    if (this.priceValue) {
      this.priceValue.textContent = this.formatMoney(this.currentVariant.price);
    }
    if (this.compareValue) {
      const compare = this.currentVariant.compare_at_price;
      if (compare && compare > this.currentVariant.price) {
        this.compareValue.textContent = this.formatMoney(compare);
        this.compareValue.hidden = false;
      } else {
        this.compareValue.textContent = '';
        this.compareValue.hidden = true;
      }
    }
  }

  formatMoney(cents) {
    const moneyFormat = window.theme?.settings?.money_format;
    if (typeof window.formatPrice === 'function' && moneyFormat) {
      return window.formatPrice(cents, moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  updateSubmitState() {
    if (!this.submitButton || !this.submitLabel) return;

    const defaultLabel = this.submitButton.dataset.defaultLabel;
    const unavailableLabel = this.submitButton.dataset.unavailableLabel;
    const selectLabel = this.submitButton.dataset.selectLabel;

    if (this.selectedOptions.some(v => v == null)) {
      this.submitButton.disabled = true;
      this.submitLabel.textContent = selectLabel;
      return;
    }

    if (!this.currentVariant || !this.currentVariant.available) {
      this.submitButton.disabled = true;
      this.submitLabel.textContent = unavailableLabel;
      return;
    }

    this.submitButton.disabled = false;
    this.submitLabel.textContent = defaultLabel;
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
    this.errorEl.textContent = '';
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  async addBundleToCart() {
    if (!this.currentVariant || !this.currentVariant.available) return;

    this.submitButton.disabled = true;
    this.submitButton.classList.add('is-loading');
    const originalLabel = this.submitLabel.textContent;

    const items = [{ id: this.currentVariant.id, quantity: 1 }];

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const sections = cart?.getSectionsToRender?.().map(s => s.section) || [];

    const body = {
      items,
      sections,
      sections_url: window.location.pathname,
    };

    try {
      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        const message = data.description || data.message || (this.submitButton?.dataset.errorLabel || '');
        this.showError(message);
        return;
      }

      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(data);
      }

      if (typeof window.updateCartCounters === 'function') {
        window.updateCartCounters();
      }

      this.close();
    } catch (error) {
      console.error('[bundle-drawer] Add to cart failed:', error);
      this.showError(error.message || (this.submitButton?.dataset.errorLabel || ''));
    } finally {
      this.submitButton.classList.remove('is-loading');
      this.updateSubmitState();
      if (this.submitLabel && this.submitButton.disabled) {
        this.submitLabel.textContent = originalLabel;
      }
    }
  }
}

customElements.define('bundle-drawer', BundleDrawer);
